import type { MouseEvent } from "react";
import { styled } from "@pigment-css/react";
import { CalendarDays, Checkmark, ChevronRightSmall, UserOutline } from "@acto/web-common/components";
import { useLocale } from "@acto/web-common/locales";
import { formatDayMonth, toLocalIsoDate } from "@acto/taskpane/field-sales-2/utils/format";
import type { Todo } from "@acto/taskpane/core/domain";

const SectionHeading = styled("h2")(({ theme }) => ({
  margin: 0,
  padding: `0 ${theme.fieldSalesTwo.spacing[8]}`,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[17],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

// Surface mirrors SummaryCard (RecapReadView): same literal background/border, keep in sync.
const Card = styled("div")(({ theme }) => ({
  background: "#f8f8f7",
  border: "1px solid rgba(255, 255, 255, 0.64)",
  borderRadius: theme.fieldSalesTwo.radius[32],
  overflow: "hidden",
}));

const TodoList = styled("ul")({
  listStyle: "none",
  margin: 0,
  padding: 0,
  display: "flex",
  flexDirection: "column",
});

// overflow/max-height stay so the check-off animation can collapse the row to zero on delete.
const TodoItem = styled("li")<{ checkingOff: boolean }>(({ theme }) => ({
  overflow: "hidden",
  maxHeight: "12rem",
  transition: "max-height 320ms ease, opacity 280ms ease, transform 280ms ease",
  // Inset hairline between rows: starts at the todo text (row padding-left + checkbox + gap) and
  // stops at the row's right padding, rather than spanning the whole card edge-to-edge.
  "&:not(:last-child)": { position: "relative" },
  "&:not(:last-child)::after": {
    content: '""',
    position: "absolute",
    left: `calc(${theme.fieldSalesTwo.spacing[18]} + ${theme.fieldSalesTwo.spacing[20]} + ${theme.fieldSalesTwo.spacing[12]})`,
    right: theme.fieldSalesTwo.spacing[18],
    bottom: 0,
    height: "1px",
    background: theme.fieldSalesTwo.colors.neutral[200],
  },
  variants: [
    {
      props: { checkingOff: true },
      style: { maxHeight: 0, opacity: 0, transform: "translateX(8px)" },
    },
  ],
}));

const TodoRow = styled("button")<{ interactive: boolean }>(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.fieldSalesTwo.spacing[12],
  width: "100%",
  boxSizing: "border-box",
  textAlign: "left",
  WebkitTapHighlightColor: "transparent",
  padding: `${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[18]}`,
  background: "transparent",
  border: "none",
  transition: "background 120ms ease",
  variants: [
    {
      props: { interactive: true },
      style: {
        cursor: "pointer",
        // Press feedback only, deliberately no hover: a hover tint on these flat transparent rows
        // reads as a prominent highlight.
        "&:active": { background: theme.fieldSalesTwo.colors.surface.glassStrong },
      },
    },
  ],
}));

// Renders as a plain <span> in read-only contexts (creation flow) and as a <button> when the list
// is completable (static recap view, via `onComplete`). It fills with a checkmark both while a
// deleted todo animates out (the "check off" deletion animation) and when checked as done.
const Checkbox = styled("span")<{ checked: boolean; completable: boolean }>(({ theme }) => ({
  flexShrink: 0,
  width: theme.fieldSalesTwo.spacing[20],
  height: theme.fieldSalesTwo.spacing[20],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  // Matches the todo list item's checkbox (squircle, not a circle).
  borderRadius: theme.fieldSalesTwo.radius[10],
  border: `1.5px solid ${theme.fieldSalesTwo.colors.neutral[500]}`,
  background: "transparent",
  transition: "background 160ms ease, border-color 160ms ease",
  color: theme.fieldSalesTwo.colors.status.success.main,
  "& svg": { width: 12, height: 12, opacity: 0, transition: "opacity 160ms ease" },
  variants: [
    {
      props: { checked: true },
      style: {
        "& svg": { opacity: 1 },
      },
    },
    {
      props: { completable: true },
      style: {
        cursor: "pointer",
        WebkitTapHighlightColor: "transparent",
        "&:hover": { borderColor: theme.fieldSalesTwo.colors.neutral[600] },
      },
    },
  ],
}));

// Stacks the title over the optional due-date line.
const TodoText = styled("span")({
  flex: "1 1 auto",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
});

const TodoTitle = styled("span")<{ done: boolean }>(({ theme }) => ({
  minWidth: 0,
  // Up to 3 lines, then truncate with an ellipsis.
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 3,
  overflow: "hidden",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[900],
  variants: [
    {
      // Done: struck through + dimmed so it reads as completed and not actionable — matches the
      // completed row on the Todos page (TodoListItem).
      props: { done: true },
      style: {
        color: theme.fieldSalesTwo.colors.neutral[800],
        textDecoration: "line-through",
      },
    },
  ],
}));

// Due date shown beneath the title once set — plain muted text, matching the
// due-date style on the Aufgaben todo rows.
const DueDate = styled("span")(({ theme }) => ({
  marginTop: theme.fieldSalesTwo.spacing[4],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[440],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

const MetaRow = styled("span")(({ theme }) => ({
  marginTop: theme.fieldSalesTwo.spacing[4],
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[440],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  color: theme.fieldSalesTwo.colors.neutral[700],
}));

const MetaItem = styled("span")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  "& svg": {
    flexShrink: 0,
    width: theme.fieldSalesTwo.spacing[18],
    height: theme.fieldSalesTwo.spacing[18],
  },
}));

const TodoChevron = styled(ChevronRightSmall)(({ theme }) => ({
  flexShrink: 0,
  // Center against the full row height (title + optional due-date line), while the
  // row itself stays flex-start so the checkbox aligns to the first title line.
  alignSelf: "center",
  width: 20,
  height: 20,
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

interface RecapSummaryTodoListProps {
  /** Section heading shown above the list (e.g. "Aufgaben" for open, "Erledigt" for done). */
  heading: string;
  todos: Todo[];
  /** Ids currently animating out via the check-off deletion animation (flow only). */
  checkingOffIds?: string[];
  /** Ids to render as checked on top of the server state (optimistic completion overlay). */
  checkedIds?: string[];
  /** Open the full-screen editor for a todo. Omit for a read-only (non-interactive) list. */
  onSelect?: (id: string) => void;
  /**
   * Complete a todo by tapping its checkbox. When provided, open (not-yet-done) todos get an
   * interactive checkbox. Omit in the creation flow, where todos are not completable by design.
   */
  onComplete?: (id: string) => void;
  /** Accessible label for the completable checkbox button. */
  completeLabel?: string;
  /**
   * Render the list as completed todos: no checkbox, struck-through title, non-interactive — so the
   * user can read what's done but can't change it. Used for the "Erledigt" bucket.
   */
  done?: boolean;
  /**
   * Read-only open todos: no checkbox and non-interactive (but not struck through) — for todos
   * assigned to someone else, which the viewer may see but not complete or edit. Pass without
   * `onSelect`/`onComplete`.
   */
  readOnly?: boolean;
}

export function RecapSummaryTodoList({
  heading,
  todos,
  checkingOffIds = [],
  checkedIds = [],
  onSelect,
  onComplete,
  completeLabel,
  done = false,
  readOnly = false,
}: RecapSummaryTodoListProps) {
  const { locale } = useLocale();

  if (todos.length === 0) return null;

  return (
    <>
      <SectionHeading>{heading}</SectionHeading>
      <Card>
        <TodoList>
          {todos.map((todo) => {
            // Done rows are read-only: no checkbox, struck title, no edit affordance.
            if (done) {
              return (
                <TodoItem key={todo.id} checkingOff={false}>
                  <TodoRow as="div" interactive={false}>
                    <TodoText>
                      <TodoTitle done>{todo.description ?? todo.title}</TodoTitle>
                    </TodoText>
                  </TodoRow>
                </TodoItem>
              );
            }

            const checkingOff = checkingOffIds.includes(todo.id);
            const isChecked = checkedIds.includes(todo.id) || todo.isCompleted();
            const canComplete = !!onComplete && !isChecked;
            // The api rejects description and due-date edits once a todo is COMPLETED.
            const canSelect = !!onSelect && !isChecked && !checkingOff;
            return (
              <TodoItem key={todo.id} checkingOff={checkingOff}>
                <TodoRow
                  as={canSelect ? "button" : "div"}
                  interactive={canSelect}
                  {...(canSelect ? { type: "button" as const, onClick: () => onSelect?.(todo.id) } : {})}
                >
                  {!readOnly && (
                    <Checkbox
                      as={canComplete ? "button" : "span"}
                      checked={checkingOff || isChecked}
                      completable={canComplete}
                      {...(canComplete
                        ? {
                            type: "button" as const,
                            "aria-label": completeLabel,
                            // The checkbox sits inside the row button, so a check-off would bubble
                            // into `onSelect` and open the editor on the todo being completed.
                            onClick: (event: MouseEvent) => {
                              event.stopPropagation();
                              onComplete?.(todo.id);
                            },
                          }
                        : { "aria-hidden": true })}
                    >
                      <Checkmark />
                    </Checkbox>
                  )}
                  <TodoText>
                    <TodoTitle done={false}>{todo.description ?? todo.title}</TodoTitle>
                    {readOnly ? (
                      // Own/unassigned todos show no assignee name — whose they are is already implied.
                      <MetaRow>
                        {todo.assignee?.displayName && (
                          <MetaItem>
                            <UserOutline aria-hidden="true" />
                            {todo.assignee.displayName}
                          </MetaItem>
                        )}
                        {todo.assignee?.displayName && todo.dueDate && <span aria-hidden="true">·</span>}
                        {todo.dueDate && (
                          <MetaItem>
                            <CalendarDays aria-hidden="true" />
                            {formatDayMonth(toLocalIsoDate(todo.dueDate), locale)}
                          </MetaItem>
                        )}
                      </MetaRow>
                    ) : (
                      todo.dueDate && <DueDate>{formatDayMonth(toLocalIsoDate(todo.dueDate), locale)}</DueDate>
                    )}
                  </TodoText>
                  {canSelect && <TodoChevron />}
                </TodoRow>
              </TodoItem>
            );
          })}
        </TodoList>
      </Card>
    </>
  );
}
